/**
 * Revision count + stored snapshots for invoice Overview / detail.
 * Never invents history rows — missing archive means count 0 and empty list.
 */

export function parseInvoiceMetadata(invoice) {
  if (!invoice || typeof invoice !== 'object') return {}
  let meta = invoice.metadata
  if (typeof meta === 'string') {
    try { meta = JSON.parse(meta) } catch (_) { return {} }
  }
  if (!meta || typeof meta !== 'object' || Array.isArray(meta)) return {}
  return meta
}

function positiveInt(value) {
  const n = Number(value)
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0
}

export function isRevisedStatus(status) {
  return String(status || '').trim().toLowerCase() === 'revised'
}

/**
 * Best available count from invoice fields, metadata, or loaded history.
 * Does not assume "Revised" means 1 unless a stored number exists.
 */
export function getInvoiceRevisionCount(invoice, historyRows) {
  const inv = invoice && typeof invoice === 'object' ? invoice : {}
  const meta = parseInvoiceMetadata(inv)
  const historyLen = Array.isArray(historyRows) ? historyRows.length : 0
  const metaRevs = Array.isArray(meta.revisions) ? meta.revisions.length : 0
  return Math.max(
    positiveInt(inv.revision_count),
    positiveInt(inv.revise_count),
    positiveInt(meta.revision_count),
    positiveInt(meta.revise_count),
    historyLen,
    metaRevs,
  )
}

export function formatStatusWithRevisionCount(status, count) {
  const label = status == null || status === '' ? '—' : String(status)
  if (count > 0 && isRevisedStatus(status)) return `Revised ×${count}`
  return label
}

export function formatRevisionTimesLabel(count) {
  if (!count) return ''
  return count === 1 ? 'Revised 1 time' : `Revised ${count} times`
}

function pickPath(...candidates) {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function parseDocUrls(row) {
  let urls = row && (row.document_urls || row.documentUrls)
  if (typeof urls === 'string') {
    try { urls = JSON.parse(urls) } catch (_) { return {} }
  }
  return urls && typeof urls === 'object' && !Array.isArray(urls) ? urls : {}
}

function parseSnapshot(raw) {
  if (!raw) return null
  if (typeof raw === 'string') {
    try { raw = JSON.parse(raw) } catch (_) { return null }
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  return raw
}

function formatWhen(raw) {
  if (!raw) return ''
  return String(raw).replace(/T/, ' ').replace(/\..+/, '')
}

function formatMoney(value) {
  if (value === null || value === undefined || value === '') return ''
  const n = Number(value)
  if (!Number.isFinite(n)) return String(value)
  return n.toLocaleString('en-IN', { maximumFractionDigits: 2 })
}

/**
 * Normalize a revision/history/metadata row for the version list.
 * Only includes View/PDF when a real snapshot or file path exists.
 */
export function normalizeRevisionSnapshot(row, index = 0) {
  if (!row || typeof row !== 'object') return null
  const urls = parseDocUrls(row)
  const snapshot = parseSnapshot(
    row.snapshot
    || row.previous_snapshot
    || urls.previous_snapshot
    || urls.snapshot
    || row.previous_invoice,
  )
  const pdfPath = pickPath(
    row.pdf_url,
    row.previous_pdf,
    row.original_invoice_file_path,
    row.file,
    urls.pdf_url,
    urls.previous_pdf,
    urls.original_invoice_url,
    urls.old_file,
    snapshot && (snapshot.pdf_url || snapshot.file),
    row.revised_invoice_file_path,
    urls.revised_invoice,
  )
  const num = row.revision_number != null && row.revision_number !== ''
    ? row.revision_number
    : (row.id != null && row.id !== '' ? row.id : index + 1)
  const when = row.created_at || row.createdAt || (snapshot && snapshot.snapshot_at) || ''
  const who = row.initiated_by || row.created_by || row.updated_by
    || (snapshot && (snapshot.updated_by || snapshot.added_by)) || ''
  const oldAmount = row.old_amount != null ? row.old_amount : (snapshot && snapshot.total_amount)
  const newAmount = row.new_amount != null ? row.new_amount : row.total_amount
  return {
    id: row.id != null ? row.id : `meta-${index}`,
    revision_number: num,
    created_at: when,
    initiated_by: who,
    old_amount: oldAmount,
    new_amount: newAmount,
    revision_type: row.revision_type || '',
    change_type: row.change_type || row.amount_change_type || '',
    pdf_path: pdfPath,
    snapshot,
    source: row._source || 'history',
  }
}

export function formatRevisionRowMeta(row) {
  if (!row) return ''
  const parts = []
  if (row.created_at) parts.push(formatWhen(row.created_at))
  if (row.initiated_by) parts.push(`by ${row.initiated_by}`)
  const oldAmt = formatMoney(row.old_amount)
  const newAmt = formatMoney(row.new_amount)
  if (oldAmt && newAmt && oldAmt !== newAmt) parts.push(`₹${oldAmt} → ₹${newAmt}`)
  else if (newAmt) parts.push(`₹${newAmt}`)
  else if (oldAmt) parts.push(`₹${oldAmt}`)
  if (row.revision_type) parts.push(String(row.revision_type))
  return parts.join(' · ')
}

/**
 * Merge API history + metadata.revisions. Empty when nothing was archived.
 */
export function collectRevisionSnapshots(invoice, historyRows) {
  const seen = new Set()
  const out = []
  const push = (raw, index, source) => {
    const row = normalizeRevisionSnapshot({ ...raw, _source: source }, index)
    if (!row) return
    const key = [
      row.id,
      row.revision_number,
      row.created_at,
      row.pdf_path || '',
    ].join('|')
    if (seen.has(key)) return
    seen.add(key)
    out.push(row)
  }

  const history = Array.isArray(historyRows) ? historyRows : []
  history.forEach((row, idx) => push(row, idx, 'history'))

  const meta = parseInvoiceMetadata(invoice)
  const metaRows = Array.isArray(meta.revisions) ? meta.revisions : []
  metaRows.forEach((row, idx) => push(row, history.length + idx, 'metadata'))

  out.sort((a, b) => {
    const na = Number(a.revision_number)
    const nb = Number(b.revision_number)
    if (Number.isFinite(na) && Number.isFinite(nb) && na !== nb) return na - nb
    const ta = Date.parse(a.created_at || '') || 0
    const tb = Date.parse(b.created_at || '') || 0
    if (ta !== tb) return ta - tb
    return String(a.id).localeCompare(String(b.id))
  })
  return out
}

export function snapshotFieldRows(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return []
  const labels = [
    ['invoice_id', 'Invoice'],
    ['status', 'Status'],
    ['invoice_date', 'Invoice date'],
    ['due_date', 'Due date'],
    ['client', 'Client'],
    ['client_gst', 'Client GST'],
    ['category', 'Category'],
    ['vertical', 'Vertical'],
    ['document_type', 'Document type'],
    ['billing_month', 'Billing month'],
    ['po_number', 'PO number'],
    ['base_amount', 'Base amount'],
    ['gst', 'GST'],
    ['total_amount', 'Total'],
    ['amount_with_gst', 'Amount with GST'],
    ['paid_amount', 'Paid'],
    ['description', 'Description'],
    ['comment', 'Comment'],
  ]
  return labels
    .filter(([key]) => snapshot[key] != null && snapshot[key] !== '')
    .map(([key, label]) => {
      const raw = snapshot[key]
      const money = /amount|gst$/i.test(key) && key !== 'client_gst'
      return {
        key,
        label,
        value: money ? `₹${formatMoney(raw)}` : String(raw),
      }
    })
}
